import React, { useCallback, useState } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { createUseStyles } from 'react-jss'
import SplitPane from 'react-split-pane'
import PropTypes from 'prop-types'
import { Drawer, Tabs, Icon } from 'antd'
import humps from 'humps'
import moment from 'moment'
import { useAsync } from '../../hooks'
import { ListError } from '../Common'
import PDFPreview from '../Common/PDFPreview'
import api from '../../api'
import Loading from '../Loading'
import ReportParsingIssuesForm from './ReportParsingIssuesForm'
import ReportPatientHeader from './ReportPatientHeader'
import Form from './Form'

const { TabPane } = Tabs

const useStyles = createUseStyles({
  app: { display: 'flex', alignItems: 'stretch', flex: '1 1 auto' },
  pdfBox: {
    maxWidth: 'calc(100% - 456px)',
    flexGrow: '1',
    minWidth: '100%',
    position: 'relative',
    height: 'calc(100vh - 96px - 68px)',
    overflowY: 'scroll',
    overflowX: 'hidden',
    '& canvas': {
      maxWidth: '100%',
    },
  },
  formBox: {
    position: 'relative',
    overflowY: 'scroll',
    height: 'calc(100vh - 96px - 68px)',
    padding: '0 16px',
    '& > form': {
      marginTop: '-16px',
    },
  },
})

export default function EditReport({ isV2Report = false }) {
  const { id } = useParams()
  const [refetchTrigger, setRefetchTrigger] = useState(0)
  const [issuesFormFields, setIssuesFormFields] = useState({
    errorFields: [],
    observations: '',
  })
  const [issuesFormVisible, setIssuesFormVisible] = useState(false)
  const [issuesFormSubmitted, setIssuesFormSubmitted] = useState(false)
  const getReport = useCallback(
    () => api.reports.getReportForm(id, isV2Report),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, isV2Report, refetchTrigger],
  )
  const { error, pending: loading, value: reportForm } = useAsync(getReport)

  const classes = useStyles()
  const handleRefetch = useCallback(() => {
    setRefetchTrigger(prev => prev + 1)
  }, [])
  const history = useHistory()

  const goBack = () => {
    history.goBack()
  }

  const getRawReport = useCallback(
    () => api.reports.get(id, {}, isV2Report),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, isV2Report, refetchTrigger],
  )

  const {
    error: rawReportError,
    pending: rawReportLoading,
    value: rawReport,
  } = useAsync(getRawReport)

  const isIssuesFormVisible =
    !issuesFormSubmitted &&
    rawReport?.pdfExtraction.status === 'complete' &&
    rawReport.pdfExtraction.id !== null &&
    !rawReport?.biobridgeReport

  if (loading || rawReportLoading) {
    return <Loading large />
  }

  if (error || rawReportError) {
    return (
      <ListError
        message="There was an error loading this report"
        description={error.message}
      />
    )
  }

  const onIssuesFormSubmit = () => {
    setIssuesFormFields({
      errorFields: [],
      observations: '',
    })
    setIssuesFormVisible(false)
    setIssuesFormSubmitted(true)
  }

  // Toggle one of the autofilled fields.
  const toggleField = field => {
    const key = humps.camelize(field.itemName)
    setIssuesFormFields(prev => ({
      ...prev,
      errorFields: prev.errorFields.includes(key)
        ? prev.errorFields.filter(f => f !== key)
        : [...prev.errorFields, key],
    }))
  }

  const transmissions = rawReport.transmissions.sort((a, b) => {
    const aMain = a.main ? 1 : 0
    const bMain = b.main ? 1 : 0
    return bMain - aMain // main=true first
  })

  return (
    <div className={classes.app}>
      <SplitPane split="vertical">
        <div initialsize="65%" minsize="10%" className={classes.pdfBox}>
          <ReportPatientHeader
            report={reportForm}
            backPath={goBack}
            showIssuesForm={isIssuesFormVisible}
            onShowIssuesForm={() => setIssuesFormVisible(true)}
          />
          <Tabs className="tabs">
            {transmissions.length > 0
              ? transmissions.map((t, idx) => (
                  <TabPane
                    tab={
                      <span>
                        {moment(t.createdAt).format('MMM Do YYYY')}{' '}
                        {t.main && (
                          <Icon
                            type="star"
                            style={{ color: 'green', marginLeft: 4 }}
                          />
                        )}
                      </span>
                    }
                    key={`transmission-${t.id}`}
                  >
                    <PDFPreview
                      path={t.fileUrl || t.file_url || '/sample_report.pdf'}
                      showAllPages
                    />
                  </TabPane>
                ))
              : reportForm.deviceReport && (
                  <TabPane tab="Transmission" key="transmission">
                    <PDFPreview path={reportForm.deviceReport} showAllPages />
                  </TabPane>
                )}
          </Tabs>
        </div>
        <div className={classes.formBox}>
          <Form
            rawReport={rawReport}
            report={reportForm}
            isV2Report={isV2Report}
            onDataRefresh={handleRefetch}
            issuesFormFields={issuesFormFields}
            onReportField={toggleField}
            showIssuesForm={isIssuesFormVisible}
          />
        </div>
      </SplitPane>
      <Drawer
        width={500}
        title="Report parsing issues"
        placement="right"
        onClose={() => setIssuesFormVisible(false)}
        visible={issuesFormVisible}
      >
        <ReportParsingIssuesForm
          report={reportForm}
          rawReport={rawReport}
          initialValues={issuesFormFields}
          onReportField={toggleField}
          onSubmit={onIssuesFormSubmit}
        />
      </Drawer>
    </div>
  )
}

EditReport.propTypes = { isV2Report: PropTypes.bool }
EditReport.defaultProps = { isV2Report: false }
